import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts'
import { CategoricalChartState } from 'recharts/types/chart/types'
import { FiltersT } from '../../Epidemiology'
import { downloadCSV } from '../../Epidemiology/helpers'
import { DataT } from '../../Epidemiology/Map/utils/types'
import CardContainer from 'components/Dashboards/IDATrialsMapDashboard/Cards/CardContainer'
import { Download } from '@carbon/icons-react'
import { GraphColors, MapLayersColours } from '../GraphColors'
import { Select } from 'components'
import { LayerT } from 'modules/BioriskMpox/Epidemiology/Map/layers'

export type TopCountriesChartDataT = {
  label: string
  country: string
  countryName: string
  curr: number
  prev: number
  suspected: number
}[]

export const truncateText = (text: string) => {
  if (text.length > 20) return `${text.substring(0, 13)}`
  return text
}

function exportData(data: any, filters: FiltersT) {
  const headers = [
    'Country',
    'Country Code',
    'Date Start',
    'Date End',
    'Disease',
    'Cases',
  ].join(',')
  const rows: string[] = []

  Object.keys(data).forEach((_country) => {
    const row = data[_country]
    const match = _country.match(/^(.+?)\s\((\w+)\)$/)
    let areaAlpha3 = _country,
      country = _country

    if (match) {
      country = match[1]
      areaAlpha3 = match[2]
    }
    rows.push(
      [
        row.area,
        row.areaAlpha3,
        filters.dateRange.start,
        filters.dateRange.end,
        row.diseaseName,
        parseInt(row.value),
      ].join(',')
    )
  })

  const sortedRows = rows.sort((a, b) => {
    const valueA = +a.split(',')[5]
    const valueB = +b.split(',')[5]
    return valueB - valueA
  })

  const fileName = `TopCountries_${filters.country}_Types_${filters.dateRange.start}_${filters.dateRange.end}.csv`
  downloadCSV(headers, sortedRows, fileName)
}

function transformForStackedBarChart(data: DataT[], topN: number | null) {
  const resultMap = {} as Record<string, any>
  data.forEach((entry) => {
    const disease = entry.diseaseName.toString()
    const value = Math.abs(parseFloat(entry.value))

    if (!resultMap[disease]) {
      resultMap[disease] = { disease, total: 0 }
    }
    resultMap[disease].total = (resultMap[disease].total || 0) + value
  })

  const stackedData = Object.values(resultMap)
    .sort((a, b) => b.total - a.total)
    .slice(0, topN ?? Infinity)
  return stackedData
}

const StackedDiseasesByCountry = ({
  data,
  selectCountry,
  filters,
  layers,
}: {
  data: DataT[]
  selectCountry?: (country: string) => void
  filters: FiltersT
  layers?: {
    mapLayers: string[] | undefined
    selectedLayer: LayerT
    handleLegendClick: (newLayer: LayerT) => void
  }
}) => {
  const [expanded, setExpanded] = useState(false)
  const [type, setType] = useState({ value: 'types', label: 'Types' })

  const { selectedLayer } = layers || {}

  const allowedDiseases =
    type.value === 'types'
      ? [50141, 50352, 50353, 50354, 114827]
      : [
          114855, 114856, 114873, 114874, 114875, 114857, 114879, 114880,
          114859, 114858, 114877, 114860, 114869, 114883, 114861, 114881,
          114862, 114872, 114870, 114868, 114864, 122981, 114867, 114871,
          114865, 114866,
        ]

  const filteredData = (
    filters.country !== 'global'
      ? data.filter(({ areaAlpha3 }) => areaAlpha3 === filters.country)
      : data
  )
    .filter(({ disease }) => allowedDiseases.includes(disease))
    .filter(
      ({ diseaseName }) => !selectedLayer || selectedLayer === diseaseName
    )

  const diseaseByCountry = transformForStackedBarChart(
    filteredData,
    expanded ? null : filters.nCountries
  )

  const noData = !diseaseByCountry.some((item) => !!item.disease)

  function handleClick(e: CategoricalChartState) {
    const label = e?.activeLabel
    if (label) {
      layers?.handleLegendClick(label as LayerT)
    }
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const items = payload.sort((a: any, b: any) => {
        return b.value - a.value
      })
      return (
        <Box
          p={2}
          borderWidth='1px'
          borderRadius='md'
          bg='white'
          shadow='md'
          fontSize={'sm'}
        >
          <Flex gap='4px'>
            <Text fontWeight={'semibold'}>{label}</Text>
          </Flex>
          {items.map((entry: any, index: number) => {
            return (
              <Flex gap='8px' key={entry.name} alignItems={'center'}>
                <Text textTransform='capitalize'>{entry.name}:</Text>
                <Text>{entry.value}</Text>
              </Flex>
            )
          })}
        </Box>
      )
    }

    return null
  }

  return (
    <CardContainer
      minWidth='350px'
      expanded={expanded}
      setExpanded={setExpanded}
      width='100%'
    >
      <Flex
        w='90%'
        gap={2}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Flex alignItems={'center'} gap={2}>
          <Text fontWeight={'semibold'} whiteSpace={'nowrap'} fontSize='sm'>
            Cases by{' '}
          </Text>
          <Select
            value={type}
            isMulti={false}
            isSearchable={false}
            options={[
              { value: 'types', label: 'Types' },
              { value: 'serotypes', label: 'Serotypes' },
            ]}
            color='#000'
            onChange={setType}
            fontSize={'10px'}
            height='26px'
          />
        </Flex>
        <Flex alignItems={'center'} gap={2}>
          <Button
            onClick={() => exportData(data, filters)}
            variant={'ghost'}
            w={'24px'}
            h={'24px'}
            p={0}
          >
            <Download size={16} />
          </Button>
        </Flex>
      </Flex>
      <Box w={'100%'} h={'100%'} pb='1rem'>
        {noData ? (
          <Flex justifyContent={'center'} alignItems='center' h='100%'>
            <Text>No data for this filters</Text>
          </Flex>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={diseaseByCountry}
              layout='vertical'
              margin={{
                top: 20,
                right: 50,
                left: expanded ? 40 : 0,
                bottom: -30,
              }}
              onClick={handleClick}
            >
              <Tooltip content={<CustomTooltip />} />
              <XAxis
                type='number'
                dataKey={'total'}
                fontSize={expanded ? '12px' : '8px'}
                domain={[0, 'auto']}
              />
              <YAxis
                dataKey='disease'
                type='category'
                width={60}
                fontSize={expanded ? '12px' : '8px'}
                ticks={diseaseByCountry.map((item) => item.disease)}
                interval={0}
              />

              <Bar dataKey='total' stackId='a' fill={GraphColors[4]}>
                {diseaseByCountry.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.country}`}
                    fill={
                      MapLayersColours.specific[entry.disease] ??
                      MapLayersColours.extra[
                        index % MapLayersColours.extra.length
                      ] ??
                      '#808080'
                    }
                  />
                ))}
                <LabelList
                  fontSize={'10px'}
                  dataKey={'total'}
                  position='right'
                  formatter={(v: any) => v.toFixed(0)}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </CardContainer>
  )
}

export default StackedDiseasesByCountry
